import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  ProjectServiceCreateResponseJson,
  defaultLatLng,
  extendLatLngBounds,
} from '@vmspdb/definitions';
import { Paginated } from '@vmspdb/ui/domain';
import * as fromPaginatedProject from './paginated-project.reducer';

export const selectPaginatedProjectState =
  createFeatureSelector<fromPaginatedProject.PaginatedProjectState>(
    fromPaginatedProject.paginatedProjectFeatureKey,
  );

export const selectPaginatedProjects = createSelector(
  selectPaginatedProjectState,
  (state): Paginated<ProjectServiceCreateResponseJson> => state,
);

export const selectPaginatedProjectError = createSelector(
  selectPaginatedProjectState,
  (state) => state.error,
);

export interface ProjectMarker {
  lat: number;
  lng: number;
  project?: ProjectServiceCreateResponseJson;
  options: google.maps.marker.AdvancedMarkerElementOptions;
  icon: string;
}

function makeImage(icon: string) {
  const image = new Image();
  image.src = icon;
  return image;
}

export const selectPaginatedProjectMarkers = createSelector(
  selectPaginatedProjectState,
  (state) => {
    const markers: ProjectMarker[] = state.items
      .filter(
        (project) =>
          !!(
            project.location?.address?.components?.lat &&
            project.location?.address?.components?.lng
          ),
      )
      .map((project): ProjectMarker => ({
        lat: Number(project.location?.address?.components?.lat ?? 0),
        lng: Number(project.location?.address?.components?.lng ?? 0),
        project,
        icon: project.flags?.finalized
          ? 'https://maps.google.com/mapfiles/ms/micons/green-dot.png'
          : 'https://maps.google.com/mapfiles/ms/micons/red-dot.png',
        options: {
          title: project.name,
          content: makeImage(
            project.flags?.finalized
              ? 'https://maps.google.com/mapfiles/ms/micons/green-dot.png'
              : 'https://maps.google.com/mapfiles/ms/micons/red-dot.png',
          ),
        },
      }));

    if (state.refGeoPoint) {
      markers.unshift({
        ...state.refGeoPoint,
        icon: 'https://maps.gstatic.com/mapfiles/ms2/micons/ltblu-pushpin.png',
        options: {
          title: 'Reference Point',
          content: makeImage(
            'https://maps.gstatic.com/mapfiles/ms2/micons/ltblu-pushpin.png',
          ),
        },
      });
    }

    return markers;
  },
);

export const selectPaginatedProjectBoundingBox = createSelector(
  selectPaginatedProjectMarkers,
  (state) => {
    const bounds = new google.maps.LatLngBounds();
    state.forEach((marker) => bounds.extend(marker));

    if (!state.length) {
      extendLatLngBounds(defaultLatLng).forEach((latLng) =>
        bounds.extend(latLng),
      );
    } else if (state.length === 1) {
      extendLatLngBounds(state[0]).forEach((latLng) => bounds.extend(latLng));
    }

    return bounds;
  },
);
